import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { ApiError } from "@nutrayan/api-client";
import App from "./App";
import { initSentry } from "./lib/sentry";
import "./styles.css";

/**
 * Never retry a 4xx (auth/validation/not-found — a retry just re-fires the same
 * rejected call and shows up as "same API called multiple times"). Retry
 * network/5xx once so a transient blip self-heals without spamming.
 */
function retryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}

/** Never show a white screen: any boot failure renders a readable error card. */
function renderBootError(el: HTMLElement, error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  el.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#F0E9D8;font-family:system-ui,sans-serif;padding:16px">
      <div style="max-width:480px;background:#FAF6EF;border-radius:8px;padding:24px;box-shadow:0 1px 4px rgba(0,0,0,.1)">
        <h1 style="margin:0;font-size:20px;color:#1C1C1A">Admin failed to start</h1>
        <p style="color:#2C2A27;font-size:14px;line-height:1.5;margin:12px 0 0">${message}</p>
        <p style="color:#7A7570;font-size:12px;margin:12px 0 0">
          Check the browser console for details. If this mentions environment variables,
          copy <code>apps/admin/.env.example</code> to <code>apps/admin/.env.local</code>.
        </p>
      </div>
    </div>`;
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

try {
  initSentry();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: retryQuery,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });

  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
        <Toaster position="top-right" />
      </QueryClientProvider>
    </React.StrictMode>,
  );
} catch (error) {
  // eslint-disable-next-line no-console
  console.error("[admin boot]", error);
  renderBootError(rootEl, error);
}
